import type { KanbanPeriod, TaskStatus, TaskSummary } from '@pm/schemas';
import {
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';

export const PERIOD_OPTIONS: { value: KanbanPeriod; label: string }[] = [
  { value: 'today', label: 'Prazo até hoje' },
  { value: 'week', label: 'Esta semana' },
  { value: 'two_weeks', label: 'Esta e a próxima semana' },
  { value: 'month', label: 'Este mês' },
  { value: 'all', label: 'Todos os prazos' },
];

/** Semana de segunda a domingo. */
const WEEK = { weekStartsOn: 1 } as const;

export interface PeriodBounds {
  /** Não iniciadas com prazo até aqui (atrasadas incluídas); null = sem limite. */
  dueTo: Date | null;
  /** Concluídas a partir daqui ("Todos os prazos" mostra os últimos 30 dias). */
  completedFrom: Date;
}

export function periodBounds(period: KanbanPeriod, now = new Date()): PeriodBounds {
  switch (period) {
    case 'today':
      return { dueTo: endOfDay(now), completedFrom: startOfDay(now) };
    case 'week':
      return { dueTo: endOfWeek(now, WEEK), completedFrom: startOfWeek(now, WEEK) };
    case 'two_weeks':
      return { dueTo: endOfWeek(addWeeks(now, 1), WEEK), completedFrom: startOfWeek(now, WEEK) };
    case 'month':
      return { dueTo: endOfMonth(now), completedFrom: startOfMonth(now) };
    case 'all':
      return { dueTo: null, completedFrom: subDays(startOfDay(now), 30) };
  }
}

/** Em andamento, aguardando, bloqueada e em aprovação: trabalho ativo, sempre aparece. */
export const ACTIVE_STATUSES: TaskStatus[] = [
  'IN_PROGRESS',
  'WAITING',
  'BLOCKED',
  'PENDING_APPROVAL',
];

export interface KanbanFilterResult {
  visible: TaskSummary[];
  /** Não iniciadas escondidas por prazo depois do período. */
  outOfPeriod: number;
  /** Não iniciadas sem prazo escondidas. */
  noDue: number;
}

/**
 * Enxuga o quadro: o período vale para as não iniciadas (pelo prazo) e para as concluídas (já
 * vêm filtradas pela data de conclusão na consulta). O trabalho ativo nunca some.
 */
export function filterOpenTasks(
  open: TaskSummary[],
  bounds: PeriodBounds,
  includeNoDue: boolean,
): KanbanFilterResult {
  let outOfPeriod = 0;
  let noDue = 0;
  const limit = bounds.dueTo?.getTime() ?? null;
  const visible = open.filter((t) => {
    if (t.status !== 'NOT_STARTED') return true;
    if (!t.due_at) {
      if (includeNoDue) return true;
      noDue++;
      return false;
    }
    if (limit === null || new Date(t.due_at).getTime() <= limit) return true;
    outOfPeriod++;
    return false;
  });
  return { visible, outOfPeriod, noDue };
}
